/**
 * Admin account-merge tool. Plan §Account Lifecycle Edge Cases.
 *
 * Workflow:
 *   1. Search for the primary user (the account that will SURVIVE).
 *   2. Search for the duplicate (gets hard-deleted at the end).
 *   3. Type the identity-proof evidence note (>= 10 chars, server-
 *      enforced).
 *   4. Type "merge" to confirm + click Execute.
 *
 * The form intentionally requires the admin to look up BOTH users
 * before showing the proof-note + confirm controls. That's a small
 * speed bump that prevents typo-based wrong-row merges.
 *
 * Server returns 409 with a detail message on constraint violations
 * (e.g. both users already have a match with the same target — the
 * post-merge canonical-ordering check would fail). Surface the detail
 * inline so the admin can resolve manually before retrying.
 */

import { useState } from 'react';
import { useMergeAccountsMutation } from '../api/merge.js';
import { type UserSearchResult, useUserSearchQuery } from '../api/users.js';

export function AccountMergeRoute() {
  const [primary, setPrimary] = useState<UserSearchResult | null>(null);
  const [duplicate, setDuplicate] = useState<UserSearchResult | null>(null);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Merge accounts</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Support tool for duplicate-account cases (different IdPs, lost-recovery, Apple privacy-
          relay). The primary account survives; the duplicate is hard-deleted after its matches,
          messages, credits, subscriptions, photos, and reports transfer over. The audit row
          captures the identity-proof evidence so we can defend the merge in a SAR.
        </p>
      </header>

      <UserPicker
        title="1. Primary account (will survive)"
        selected={primary}
        onSelect={setPrimary}
        otherSelected={duplicate}
      />

      <UserPicker
        title="2. Duplicate account (will be hard-deleted)"
        selected={duplicate}
        onSelect={setDuplicate}
        otherSelected={primary}
      />

      {primary && duplicate && <ExecutePanel primary={primary} duplicate={duplicate} />}
    </section>
  );
}

function UserPicker({
  title,
  selected,
  onSelect,
  otherSelected,
}: {
  title: string;
  selected: UserSearchResult | null;
  onSelect: (u: UserSearchResult | null) => void;
  otherSelected: UserSearchResult | null;
}) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState('');
  const search = useUserSearchQuery(active);

  return (
    <section className="rounded-md border border-neutral-200 bg-white p-4">
      <h2 className="font-medium text-neutral-900 text-sm">{title}</h2>
      {selected ? (
        <div className="mt-3 flex items-start justify-between rounded-md bg-primary-50 px-3 py-2">
          <div>
            <p className="font-medium text-neutral-900 text-sm">{selected.email}</p>
            <p className="mt-0.5 font-mono text-xs text-neutral-500">{selected.id}</p>
            <p className="mt-0.5 text-xs text-neutral-500">
              {selected.phoneE164 ?? 'no phone'} · {selected.status}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setQ('');
              setActive('');
            }}
            className="text-sm text-neutral-500 hover:text-neutral-700"
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <form
            className="mt-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setActive(q.trim());
            }}
          >
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="email, +E.164 phone, or UUID"
              className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
            />
            <button
              type="submit"
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Search
            </button>
          </form>
          {search.isLoading && <p className="mt-3 text-sm text-neutral-500">Searching…</p>}
          {search.data && search.data.results.length === 0 && (
            <p className="mt-3 text-sm text-neutral-500">No users match.</p>
          )}
          {search.data && search.data.results.length > 0 && (
            <ul className="mt-3 divide-y divide-neutral-200 rounded-md border border-neutral-200">
              {search.data.results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    disabled={otherSelected?.id === r.id}
                    onClick={() => onSelect(r)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span>
                      <span className="font-medium text-neutral-900">{r.email}</span>
                      <span className="ml-2 text-neutral-500">
                        {r.phoneE164 ?? 'no phone'} · {r.status}
                      </span>
                    </span>
                    <span className="font-mono text-xs text-neutral-400">{r.id.slice(0, 8)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function ExecutePanel({
  primary,
  duplicate,
}: {
  primary: UserSearchResult;
  duplicate: UserSearchResult;
}) {
  const [proof, setProof] = useState('');
  const [confirm, setConfirm] = useState('');
  const merge = useMergeAccountsMutation();

  if (primary.id === duplicate.id) {
    return (
      <p className="rounded-md border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
        Primary and duplicate are the same user. Pick a different duplicate above.
      </p>
    );
  }

  const valid = proof.trim().length >= 10 && confirm === 'merge';

  return (
    <section className="space-y-3 rounded-md border border-warning-200 bg-warning-50 p-4">
      <h2 className="font-semibold text-neutral-900">3. Execute merge</h2>
      <p className="text-sm text-neutral-700">
        This is irreversible. The duplicate user row + their profile + preferences + consents will
        be deleted. Their matches, messages, credits, subscriptions, photos, and reports move to the
        primary.
      </p>

      <label className="block">
        <span className="block font-medium text-neutral-700 text-sm">
          Identity-proof note (minimum 10 chars; audit-logged)
        </span>
        <textarea
          value={proof}
          onChange={(e) => setProof(e.target.value)}
          rows={3}
          placeholder="What evidence did the caller give? Last 4 of phone, recent match memory, an admin transcript..."
          className="mt-1 w-full rounded-md border border-neutral-300 p-2 text-sm"
        />
      </label>

      <label className="block">
        <span className="block font-medium text-neutral-700 text-sm">
          Type <span className="font-mono">merge</span> to confirm
        </span>
        <input
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mt-1 w-40 rounded-md border border-neutral-300 px-3 py-2 text-sm"
        />
      </label>

      {merge.error && <p className="text-sm text-danger-700">{(merge.error as Error).message}</p>}
      {merge.data && (
        <div className="rounded-md border border-success-200 bg-success-50 p-3 text-sm text-success-700">
          <p className="font-medium">Merge complete.</p>
          <p className="mt-1">
            Record id <span className="font-mono">{merge.data.mergeRecordId}</span>. Transferred:{' '}
            {merge.data.transferSummary.matches} matches · {merge.data.transferSummary.messages}{' '}
            messages · {merge.data.transferSummary.credits} credits ·{' '}
            {merge.data.transferSummary.subscriptions} subscriptions.
          </p>
        </div>
      )}

      <button
        type="button"
        disabled={!valid || merge.isPending}
        onClick={() =>
          merge.mutate({
            primaryUserId: primary.id,
            duplicateUserId: duplicate.id,
            identityProofNote: proof.trim(),
          })
        }
        className="rounded-md bg-danger-600 px-3 py-2 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-50"
      >
        {merge.isPending ? 'Merging…' : 'Execute merge'}
      </button>
    </section>
  );
}
